// Email providers anyone can sign up to, so the domain says nothing about the person's company.
// Brand detection, signup and group registration's domain rule all read this one list.
export const FREE_EMAIL_DOMAINS = [
  // Google
  'gmail.com',
  'googlemail.com',
  // Microsoft
  'outlook.com',
  'outlook.co.uk',
  'hotmail.com',
  'hotmail.co.uk',
  'live.com',
  'live.co.uk',
  'msn.com',
  // Yahoo
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.fr',
  'yahoo.de',
  'yahoo.ca',
  'yahoo.com.au',
  'yahoo.co.in',
  'yahoo.com.br',
  'yahoo.co.jp',
  // Apple
  'icloud.com',
  'me.com',
  'mac.com',
  // Proton
  'protonmail.com',
  'proton.me',
  'pm.me',
  // UK internet providers
  'btinternet.com',
  'sky.com',
  'virginmedia.com',
  'ntlworld.com',
  'talktalk.net',
  // Others
  'aol.com',
  'zoho.com',
  'yandex.com',
  'yandex.ru',
  'mail.com',
  'email.com',
  'gmx.com',
  'gmx.de',
  'gmx.net',
  'web.de',
  'tutanota.com',
  'tutanota.de',
  'tuta.io',
  'fastmail.com',
  'fastmail.fm',
  'hey.com',
  'mailbox.org',
  'posteo.de',
  'runbox.com',
] as const;

const freeDomains = new Set<string>(FREE_EMAIL_DOMAINS);

/** The lowercased domain of `jane@acme.com`, or of a bare `acme.com`. */
export function emailDomainOf(emailOrDomain: unknown): string {
  if (typeof emailOrDomain !== 'string') return '';
  const value = emailOrDomain.trim().toLowerCase();
  return value.includes('@') ? (value.split('@').pop() ?? '') : value;
}

/** Exact match: a subdomain of a free provider counts as a company domain. */
export function isFreeEmailDomain(emailOrDomain: unknown): boolean {
  const domain = emailDomainOf(emailOrDomain);
  return domain !== '' && freeDomains.has(domain);
}

export type GuestDomainMode = 'any' | 'flag' | 'work' | 'workFlag' | 'same';

export type GuestDomainCheck =
  | { ok: true; flagged?: boolean }
  | { ok: false; reason: 'public'; message: string }
  | { ok: false; reason: 'other_domain'; domain: string; message: string };

const PUBLIC_MESSAGE = 'Use a company email address.';
const otherDomainMessage = (domain: string) => `Use an email address at ${domain}.`;

/**
 * Under 'same', the booker needs a company email too: a public address can't be compared.
 */
export function checkBookerDomain(bookerEmail: string, mode: GuestDomainMode): GuestDomainCheck {
  if (mode === 'same' && isFreeEmailDomain(bookerEmail)) {
    return { ok: false, reason: 'public', message: PUBLIC_MESSAGE };
  }
  return { ok: true };
}

/**
 * 'flag' only marks public-domain guests for review; 'work' rejects public domains, and 'workFlag'
 * also flags a work domain other than a work-domain booker's. 'same' allows only the booker's domain.
 */
export function checkGuestDomain({
  bookerEmail,
  guestEmail,
  mode,
}: {
  bookerEmail: string;
  guestEmail: string;
  mode: GuestDomainMode;
}): GuestDomainCheck {
  if (mode === 'any') return { ok: true };

  const bookerDomain = emailDomainOf(bookerEmail);
  const guestDomain = emailDomainOf(guestEmail);

  if (mode === 'flag') {
    return isFreeEmailDomain(guestDomain) ? { ok: true, flagged: true } : { ok: true };
  }

  if (isFreeEmailDomain(guestDomain))
    return { ok: false, reason: 'public', message: PUBLIC_MESSAGE };
  if (mode === 'work') return { ok: true };
  if (mode === 'workFlag') {
    const comparable = bookerDomain && !isFreeEmailDomain(bookerDomain);
    return comparable && guestDomain !== bookerDomain ? { ok: true, flagged: true } : { ok: true };
  }
  if (guestDomain !== bookerDomain) {
    return {
      ok: false,
      reason: 'other_domain',
      domain: bookerDomain,
      message: otherDomainMessage(bookerDomain),
    };
  }
  return { ok: true };
}
